{
  "title": "Connections",
  "intro": "Every source you have linked, what it is sending right now, and which one wins when two sources report the same thing.",
  "noProfile": "Create an athlete to add a connection.",
  "sources": "Sources",
  "sourcesHint": "The Kaiord extension and permissions live inside each connection",
  "unsupported": "Not available yet",
  "unsupportedHint": "Connecting these needs a server Kaiord does not run.",
  "summary": {
    "detected": "Sources detected",
    "detectedNote": "of {{total}}",
    "covered": "Types covered",
    "coveredNote": "of {{total}} types · incl. manual entry",
    "attention": "Needs attention",
    "attentionNote_one": "source",
    "attentionNote_other": "sources",
    "lastSync": "Last data in",
    "lastSyncNever": "None yet",
    "pending": "—",
    "refresh": "Refresh all",
    "refreshing": "Refreshing…",
    "refreshCooldown": "Just refreshed. Try again in a minute."
  },
  "banner": {
    "one": "Kaiord cannot read from {{name}}",
    "oneOutdated": "{{name}}'s Kaiord extension is out of date",
    "many": "{{count}} sources need attention",
    "paused": "No source is sending {{types}} right now.",
    "covered": "Nothing has stopped: every affected type also has another source switched on.",
    "noRoutes": "No data routes are switched on, so nothing has stopped arriving.",
    "since": "No new data since {{date}}."
  },
  "status": {
    "connected": "Connected",
    "installed": "Detected on load",
    "checking": "Checking…",
    "attention": "No access",
    "available": "Not connected",
    "manual": "Always on",
    "unsupported": "Not supported yet"
  },
  "detail": {
    "installed": "This extension announced itself when the page loaded. Kaiord cannot re-check it without downloading your whole export, so it may have been removed since.",
    "checking": "Asking the extension whether it still has a session.",
    "noAccess": "Kaiord could not read from this source. Signing in again on its site can restore access; if you are already signed in, the source may be temporarily unavailable.",
    "outdated": "This Kaiord extension is out of date. Update the extension to resume — signing in again will not help.",
    "disconnected": "You disconnected this source in Kaiord. The extension may still be installed.",
    "manual": "Anything you type into Kaiord yourself. Never switched off.",
    "unsupported": "Kaiord has no way to connect this account yet."
  },
  "fixAtSource": "Sign in at {{name}} ↗",
  "bridgeDetected": "{{name}} Kaiord extension · detected",
  "bridgeAnnounced": "{{name}} Kaiord extension · detected on load",
  "lastSync": "Last data received {{when}}",
  "neverSynced": "No data received yet",
  "chips": {
    "inOne": "1 type in",
    "inMany": "{{count}} types in",
    "outOne": "1 type out",
    "outMany": "{{count}} types out"
  },
  "manage": {
    "open": "Manage",
    "close": "Close",
    "sends": "Sends to Kaiord",
    "receives": "Kaiord sends back",
    "nothing": "Nothing",
    "unknown": "Unknown until the extension is running",
    "syncNow": "Sync now",
    "syncing": "Syncing…",
    "syncDone": "Sync finished",
    "syncFailed": "Sync failed. Try again in a moment.",
    "syncCooldown": "Just synced. Try again in a minute.",
    "bodyExport": "Sync Tanita → Garmin",
    "bodyExportStatus": {
      "idle": "Ready to sync",
      "reading": "Reading measurements from Tanita…",
      "parsing": "Parsing measurements…",
      "encoding": "Encoding for Garmin…",
      "uploading": "Uploading to Garmin…",
      "done": "Sync complete",
      "needsReauth": "Your mytanita.eu session expired — sign in there and try again.",
      "error": "Sync failed"
    },
    "disconnect": "Disconnect",
    "disconnectMessage": "This unlinks the source in Kaiord and switches off every route it feeds. The browser extension stays installed.",
    "reconnect": "Reconnect",
    "connect": "Connect",
    "cancel": "Cancel",
    "apiKey": "API key",
    "connecting": "Connecting…",
    "connectFailed": "Could not connect"
  },
  "routing": {
    "title": "Where each data type comes from",
    "freshnessHint": "Times below are per source — when that source last sent Kaiord anything, not this type alone.",
    "from": "From",
    "sentTo": "Also sent to",
    "nowhere": "Nowhere",
    "exportSwitches": "Destinations that receive {{type}}",
    "noSource": "No source",
    "manySources": "{{count}} sources",
    "unrankedNote": "kept side by side, none ranked first",
    "noUsableSource": "No usable source",
    "rankedUnavailableNote": "ranked to sources that are switched off — nothing is being read",
    "sourceLastSent": "{{name}} last sent data {{when}}",
    "change": {
      "open": "Change",
      "close": "Done",
      "title": "Where should Kaiord read {{type}} from?",
      "unranked": "Right now Kaiord keeps every source's {{type}} and ranks none of them; when it needs a single value it uses whichever arrived last.",
      "willRank": "Pick a source below and that changes: Kaiord will read {{type}} from the one you pick, and use the others only on days it has nothing. You can go back to keeping every source right here.",
      "ranked": "Kaiord reads {{type}} from {{name}} first, and falls back to the others on days it has nothing.",
      "stalled": "Nothing is being read for {{type}} — it is ranked to sources that are switched off. Pick one below, or go back to keeping every source.",
      "keepAll": "Keep every source",
      "switches": "Sources allowed to send {{type}}",
      "switchesHint": "Switch one on and Kaiord starts reading {{type}} from it; switch it off and it stops. The browser extension stays installed either way."
    },
    "groups": {
      "training": "Training",
      "recovery": "Recovery",
      "body": "Body"
    }
  },
  "dataTypeHints": {
    "workout": "Completed structured sessions",
    "planned-session": "What you or your coach scheduled",
    "activity": "Unstructured rides, runs and walks",
    "training-zones": "FTP, threshold HR, max HR",
    "weight": "Scale readings",
    "sleep": "Duration, stages, efficiency",
    "hrv": "Heart-rate variability, overnight or spot",
    "daily-wellness": "Steps, calories, intensity minutes",
    "body-composition": "Body fat, lean mass, body water",
    "stress": "All-day stress level",
    "strain": "Cardiovascular load for the day",
    "vitals": "Resting HR, SpO₂, respiration",
    "heart-rate-series": "Sampled heart-rate traces"
  },
  "dataTypes": {
    "workout": "Workout",
    "planned-session": "Planned Session",
    "activity": "Activity",
    "training-zones": "Training Zones",
    "weight": "Weight",
    "sleep": "Sleep",
    "hrv": "HRV",
    "daily-wellness": "Daily Wellness",
    "body-composition": "Body Composition",
    "stress": "Stress",
    "strain": "Strain",
    "vitals": "Vitals",
    "heart-rate-series": "Heart Rate Series"
  },
  "profileLink": {
    "notLinked": "{{name}} is signed in on this browser but not linked to this athlete, so nothing syncs yet.",
    "link": "Link to this athlete",
    "signIn": "{{name}} is not linked to this athlete. Sign in to {{name}} in this browser first, then link it here.",
    "linking": "Linking…",
    "linked": "Linked to this athlete as {{user}}. Unlike the rest of this page, the link is saved with the athlete, not just in this browser.",
    "unlink": "Unlink",
    "unlinking": "Unlinking…",
    "unlinked": "Unlinked from this athlete",
    "unlinkFailed": "Could not unlink from this athlete"
  },
  "sourceName": {
    "manual": "Manual Entry"
  },
  "ext": {
    "requires": "Requires the Kaiord extension",
    "install": "Install Kaiord extension",
    "bannerTitle": "The Kaiord extension is not installed",
    "bannerDetail": "Garmin, Train2Go, WHOOP, Tanita and TrainingPeaks connect through the Kaiord extension. Install it in this browser to connect them."
  },
  "scope": {
    "title": "{{name}}'s connections · in this browser",
    "titleNoName": "Connections · in this browser",
    "hint": "They are kept for this athlete in this browser only and don't sync to your other devices."
  },
  "count": {
    "none": "None connected",
    "connected_one": "{{count}} connected",
    "connected_other": "{{count}} connected"
  }
}
